import { React, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import FetchModule from '../../Share/Network/FetchModule';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import { ReactComponent as NotificationsSvg } from '../../../image/Notifications.svg';
import { ReactComponent as PinSvg } from '../../../image/Pin.svg';

import NoticeList from './NoticeList';
import PinnedNoticeList from './PinnedNoticeList';
import InfoPagination from '../../../Component/MemberComponent/InfoPagination';

function NoticeListView() {
  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [data, setData] = useState([]);
  const [pinnedData, setPinnedData] = useState([]);
  const [nowPage, setNowPage] = useState(0);
  const [useData, setUseData] = useState([]);

  // 공지사항 목록 불러오기 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postData('notice/', 'POST');

        // console.log("notice list/n", responseData);

        if (responseData.status === 200) {
          const pinned = responseData.data.filter(item => item.isFixed);
          const general = responseData.data.filter(item => !item.isFixed);
          setPinnedData(pinned);
          setData(general);
        }
        else if (responseData.status === 401) history.push('/');
      }
    )();
  }, [history])

  // 페이지 변경 시 표시할 데이터 갱신 //
  useEffect(() => {
    let newDataList = data.slice((nowPage * 16), (nowPage * 16) + 16);
    setUseData(newDataList);
  }, [nowPage, data])

  function addNoticeClick() {
    history.push('/notice/create');
  }
  
  function onClickNextPage() {
    if (nowPage + 1 < Math.ceil(data.length/16)) setNowPage(nowPage + 1);
  }

  function onClickPrevPage() {
    if ((nowPage - 1) >= 0) setNowPage(nowPage - 1);  
  }

  return (
    <TopLevelWrapper>
      <TopBarWrapper>
        <TextWrapper>
          <Title>{t('noticePage.title')}</Title>
          <Explanation>{t('noticePage.description')}</Explanation>
        </TextWrapper>
        <ButtonWrapper>
          <AddButton onClick={addNoticeClick}>{t('noticePage.addNotice')}</AddButton>
        </ButtonWrapper>
      </TopBarWrapper>
      <ContentWrapper>
        <PinnedNoticeWrapper>
          <TitleWrapper>
            <IconWrapper>
              <PinIcon />
            </IconWrapper>
            <Text fontFamily='NotoSansKR' fontSize='14' color={theme.primary} hover='default'>{t('noticePage.pinnedPostContent')}</Text>
          </TitleWrapper>
          <PinnedNoticeList data={pinnedData}/>
        </PinnedNoticeWrapper>
        <GeneralNoticeListWrapper>
          <TitleWrapper>
            <IconWrapper>
              <NotificationsIcon />
            </IconWrapper>
            <Text fontFamily='NotoSansKR' fontSize='14' color={theme.primary} hover='default'>{t('noticePage.generalPostContent')}</Text>
          </TitleWrapper>
          <NoticeList data={useData}/>
          <PaginationWrapper>
            <InfoPagination
              nowPage={nowPage}
              totalPage={Math.ceil(data.length/16)}
              nextClickFn={onClickNextPage}
              prevClickFn={onClickPrevPage}/>
          </PaginationWrapper>
        </GeneralNoticeListWrapper>
      </ContentWrapper>
    </TopLevelWrapper>
  )
}

export default NoticeListView;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-left: 20px;

  gap: 30px;

  transition: all 0.3s ease-in-out;
`;

const TopBarWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 1035px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 35px;
`;

const PinnedNoticeWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const GeneralNoticeListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 25px;

  gap: 10px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  border-radius: 2px;
  background-color: ${props => props.theme.primaryAlpha50};
`;

const PinIcon = styled(PinSvg)`
  width: 16px;
  height: 16px;

  & path { fill: ${props => props.theme.background}; }
`;

const NotificationsIcon = styled(NotificationsSvg)`
  width: 13px;
  height: 13px;

  color: ${props => props.theme.background};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.hover};
  }

  transition: all 0.3s ease-in-out;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Explanation = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 16px;
`;

const AddButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100px;
  height: 40px;

  border-radius: 7px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  @media (hover: hover){
    &:hover {
      cursor: pointer;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: 20px;

  width: 100%;

  margin-bottom: 96px;
`;
